import { isSizeVariation } from "@faire/deprecated-retailer-visitor-shared/features/PrePacks/addToCartUtils";
import { titleCaseWithSpecialCharacters } from "@faire/deprecated-retailer-visitor-shared/lib/string";
import { getVariantDescriptorSuffixDropdown } from "@faire/deprecated-retailer-visitor-shared/views/product/cart/AddToCart/OptionSelection/getVariantDescriptorSuffix";
import { SizeChartLink } from "@faire/deprecated-retailer-visitor-shared/views/product/cart/AddToCart/OptionSelection/SizeChartLink";
import { SpecialOptionPicker } from "@faire/deprecated-retailer-visitor-shared/views/product/cart/AddToCart/OptionSelection/SpecialOptionPicker";
import { VariantOptionSelectionViewState } from "@faire/deprecated-retailer-visitor-shared/views/product/cart/AddToCart/OptionSelection/useVariantOptionSelectionViewState";
import { Core } from "@faire/design-tokens";
import { trackProductInfoProductDetailsVariantSelectionClick } from "@faire/web-api--source/events/productInfo/click/productDetailsVariantSelection";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { IProductVariation } from "@faire/web-api--source/indigofair/data/IProductVariation";
import { IProductVariationMap } from "@faire/web-api--source/indigofair/data/IProductVariationMap";
import { ProductVariationType } from "@faire/web-api--source/indigofair/data/ProductVariationType";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { uniqueId } from "@faire/web/common/server-side-rendering/uniqueId";
import { Dropdown } from "@faire/web/slate-deprecated/Dropdown";
import { IOption } from "@faire/web/slate/Input/SearchDropdown";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import * as React from "react";
import styled from "styled-components";

interface ISelectorProps {
  variationType: keyof typeof ProductVariationType;
  variationName: string;
  variationDisplayType?: keyof typeof IProductVariationMap.IEntry.VariationDisplayType;
  variantOptionSelectionViewState: VariantOptionSelectionViewState;
  alt?: string;
  brand: IProductPageResponse.IPdpBrand;
  isTablet: boolean;
}

const SHORT_OPTION_LENGTH = 5;

export const SingleVariationSelector: React.FC<ISelectorProps> = ({
  variationType,
  variationName,
  variantOptionSelectionViewState,
  variationDisplayType,
  alt,
  brand,
  isTablet,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const PRE_PACK = strictLocalize({
    id: "qT1LiN",
    defaultMessage: "Prepack",
    description: {
      text: "A package that has a required set quantity of specific sizes, colors, flavors, etc., of a product.",
    },
  });
  const SINGLE_OPTION = strictLocalize({
    id: "5exANo",
    defaultMessage: "Option",
    description: { text: "A single product option." },
  });

  const SIZE_OPTION = titleCaseWithSpecialCharacters("Size");

  const viewState = variantOptionSelectionViewState;

  const getDisplayableName = (): string => {
    if (
      isSizeVariation(variationType) &&
      viewState.currentStyleHasOnlyPrePacks
    ) {
      return PRE_PACK;
    }
    if (!variationName || variationName.toLowerCase() === "default") {
      return SINGLE_OPTION;
    }
    return titleCaseWithSpecialCharacters(variationName);
  };

  const variationOptions = viewState.variationsGroupedByName[variationName];

  const selectedVariationValue =
    viewState.getSelectedVariationValue(variationName);

  const displayType = getDisplayType(
    variationName,
    viewState,
    variationOptions,
    variationDisplayType
  );

  const getVariantOption = (variation: IProductVariation): IOption => {
    const variationState = viewState.stateForAlternateVariation(variation);

    const variationName = variation.value || SINGLE_OPTION;
    const suffix = getVariantDescriptorSuffixDropdown(
      variationState,
      strictLocalize
    );
    const label = suffix ? `${variationName} ${suffix}` : variationName;

    return {
      label: label,
      value: variation.value ?? "",
    };
  };

  const displayableName = getDisplayableName();

  const dropDownOptions =
    variationOptions?.map((variation) => getVariantOption(variation)) ?? [];

  const showSizeChartLink =
    (brand.brand_size_chart && displayableName === PRE_PACK) ||
    displayableName === SIZE_OPTION;

  const showSizeChartLinkForPresetPrepack =
    !!brand.brand_size_chart && displayableName === PRE_PACK;

  const dropDownRefId = uniqueId("option-dropdown-label-id-");

  // The name field is not being used on the BE but needs to stick around
  // since there are >200k variations with default-default as their value.
  // These need to be removed, at which time we will be able to skip this check
  // since all default product options won't have any variations to select
  if (
    variationOptions?.length === 1 &&
    variationOptions[0]?.name?.toLowerCase() === "default"
  ) {
    return null;
  }
  // If there is only one option, don't show the dropdown
  if (displayType === IProductVariationMap.IEntry.VariationDisplayType.SINGLE) {
    return (
      <>
        <Row justify="space-between" align="flex-end">
          <>
            <StyledTypography color={Core.text.default}>
              {displayableName}{" "}
            </StyledTypography>
            <Typography>{selectedVariationValue}</Typography>
          </>
        </Row>
        {showSizeChartLinkForPresetPrepack ? (
          <>
            <Spacer height={8} />
            <Row>
              <SizeChartLink brand={brand} product={viewState.product} />
            </Row>
          </>
        ) : null}
        <Spacer size="3x" />
      </>
    );
  }

  if (
    displayType !== IProductVariationMap.IEntry.VariationDisplayType.DROPDOWN
  ) {
    return (
      <SpecialOptionPicker
        variationName={variationName}
        variantOptionSelectionViewState={viewState}
        variationDisplayType={displayType}
        alt={alt}
        brand={brand}
        isTablet={isTablet}
      />
    );
  }

  return (
    <Column style={{ width: "100%" }}>
      <Row justify="space-between" align="flex-end">
        <Typography as="label" htmlFor={dropDownRefId}>
          {displayableName}
        </Typography>

        {showSizeChartLink ? (
          <Column>
            <SizeChartLink brand={brand} product={viewState.product} />
            <Spacer height={6} />
          </Column>
        ) : null}
      </Row>
      <Spacer size={4} />
      <div style={{ width: "100%" }} tabIndex={1} role="button">
        <StyledDropdown
          id={dropDownRefId}
          value={selectedVariationValue}
          options={dropDownOptions}
          onSelect={(option) => {
            trackProductInfoProductDetailsVariantSelectionClick(
              displayType.toString()
            );
            viewState.handleVariationSelect(variationName, option.value);
          }}
          placeholder={
            selectedVariationValue
              ? undefined
              : strictLocalize({
                  id: "WQn7CT",
                  defaultMessage: "Unavailable",
                  description: {
                    text: "this product variation is not available",
                  },
                })
          }
          disabled={
            dropDownOptions.length === 1 &&
            viewState.hasValidOptionBasedOnSelectedVariations
          }
          disableInput
        />
      </div>
      <Spacer size="3x" />
    </Column>
  );
};

const getDisplayType = (
  variationName: string,
  viewState: VariantOptionSelectionViewState,
  variationOptions?: IProductVariation[],
  variationDisplayType?: keyof typeof IProductVariationMap.IEntry.VariationDisplayType
): keyof typeof IProductVariationMap.IEntry.VariationDisplayType => {
  if (variationDisplayType) {
    return variationDisplayType;
  }
  if (variationOptions?.length === 1) {
    return IProductVariationMap.IEntry.VariationDisplayType.SINGLE;
  }
  if (viewState.allOptionsHaveSwatches(variationName)) {
    return IProductVariationMap.IEntry.VariationDisplayType.SWATCH;
  } else if (allOptionsAreShort(variationOptions)) {
    return IProductVariationMap.IEntry.VariationDisplayType.SHORT;
  }
  return IProductVariationMap.IEntry.VariationDisplayType.DROPDOWN;
};

const allOptionsAreShort = (
  variationOptions?: IProductVariation[]
): boolean => {
  return (
    variationOptions?.every(
      (variation) =>
        variation.value && variation.value.length <= SHORT_OPTION_LENGTH
    ) ?? false
  );
};

const StyledDropdown = styled(Dropdown)`
  width: 100%;

  input {
    text-align: center;
  }
`;

const StyledTypography = styled(Typography)`
  white-space: pre;
`;
